import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { getEntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { type CrmObject } from '@/types/crm';

import { useCategorisedAssociations } from './merge-association-groups';
import {
  AssocAutoResolvedList,
  AssocConflictPickers,
  AssocMatchingList,
} from './merge-associations';
import {
  CollapsibleSection,
  ConflictRow,
  MergePreviewCollapsible,
  MergePreviewPanel,
  MultiRow,
  PropertyValueRow,
} from './merge-step2-sub-components';
import { useMergeWizard } from './MergeWizardContext';
import {
  type MultiPropertyResolution,
  type SinglePropertyResolution,
} from './types';
import { useMergedPreviewObject } from './useMergedPreview';
import { useMergePropertyResolution } from './useMergePropertyResolution';

export interface MergeStep2ResolvePropertiesProps {
  canonicalObject: CrmObject | undefined;
  sourceObject: CrmObject | undefined;
  entityTypeName: string;
}

const EMPTY_OBJECT: CrmObject = {
  id: '',
  org_id: 0,
  entity_id: '',
  entity_type_name: 'ahoy_contact',
  properties: {},
  last_event_seq: 0,
  deleted_at: null,
};

export function MergeStep2ResolveProperties({
  canonicalObject,
  sourceObject,
  entityTypeName,
}: MergeStep2ResolvePropertiesProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id ?? 0;

  const {
    singlePropertyResolutions,
    multiPropertyResolutions,
    setSinglePropertyResolution,
    setMultiPropertyResolution,
  } = useMergeWizard();

  const { schema, loading: schemaLoading } = usePropertySchema(
    orgId,
    entityTypeName
  );

  const resolution = useMergePropertyResolution(
    canonicalObject ?? EMPTY_OBJECT,
    sourceObject ?? EMPTY_OBJECT,
    schema
  );

  const initializedForRef = useRef<string | null>(null);
  const pairKey = `${canonicalObject?.entity_id ?? ''}:${sourceObject?.entity_id ?? ''}`;

  useEffect(() => {
    if (!resolution.multiValue.length) {
      return;
    }
    if (initializedForRef.current === pairKey) {
      return;
    }
    initializedForRef.current = pairKey;
    for (const multi of resolution.multiValue) {
      setMultiPropertyResolution(multi.propertyName, multi);
    }
  }, [resolution.multiValue, pairKey, setMultiPropertyResolution]);

  const detailOrderKeys = useMemo(() => {
    const cfg = getEntityTypeConfig(entityTypeName);
    return (cfg.detailFieldOrder ?? [])
      .filter(f => f.type === 'property')
      .map(f => f.key);
  }, [entityTypeName]);

  const orderedConflicts = useMemo(() => {
    const conflictMap = new Map(
      resolution.conflicts.map(r => [r.propertyName, r])
    );
    const displayed = new Set<string>();
    const ordered: SinglePropertyResolution[] = [];

    for (const r of resolution.displayNameConflicts) {
      if (conflictMap.has(r.propertyName) && !displayed.has(r.propertyName)) {
        const resolved = conflictMap.get(r.propertyName);
        if (resolved !== undefined) {
          ordered.push(resolved);
        }
        displayed.add(r.propertyName);
      }
    }

    for (const key of detailOrderKeys) {
      const r = conflictMap.get(key);
      if (r && !displayed.has(key)) {
        ordered.push(r);
        displayed.add(key);
      }
    }

    const remaining = resolution.conflicts
      .filter(r => !displayed.has(r.propertyName))
      .sort((a, b) => a.propertyName.localeCompare(b.propertyName));
    ordered.push(...remaining);

    return ordered;
  }, [resolution.conflicts, resolution.displayNameConflicts, detailOrderKeys]);

  const previewObject = useMergedPreviewObject(
    canonicalObject,
    sourceObject,
    schema
  );

  const {
    conflicts: assocConflicts,
    autoResolved: assocAutoResolved,
    matching: assocMatching,
  } = useCategorisedAssociations(canonicalObject, sourceObject, orgId);

  const handleSelectWinner = useCallback(
    (
      propertyName: string,
      winner: 'canonical' | 'source',
      conflict: SinglePropertyResolution
    ) => {
      setSinglePropertyResolution(propertyName, { ...conflict, winner });
    },
    [setSinglePropertyResolution]
  );

  const handleToggleMultiItem = useCallback(
    (
      propertyName: string,
      side: 'canonical' | 'source',
      index: number,
      multi: MultiPropertyResolution
    ) => {
      const current = multiPropertyResolutions[propertyName] ?? multi;
      const nextCanonicalItems =
        side === 'canonical'
          ? current.canonicalItems.map((it, i) =>
              i === index ? { ...it, keep: !it.keep } : it
            )
          : current.canonicalItems;
      const nextSourceItems =
        side === 'source'
          ? current.sourceItems.map((it, i) =>
              i === index ? { ...it, keep: !it.keep } : it
            )
          : current.sourceItems;

      setMultiPropertyResolution(propertyName, {
        ...current,
        canonicalItems: nextCanonicalItems,
        sourceItems: nextSourceItems,
      });
    },
    [multiPropertyResolutions, setMultiPropertyResolution]
  );

  // Early returns after all hooks
  if (!canonicalObject || !sourceObject) {
    return (
      <div className='text-body-small text-[var(--color-text-tertiary)]'>
        Loading records…
      </div>
    );
  }

  if (schemaLoading) {
    return (
      <div className='text-body-small text-[var(--color-text-tertiary)]'>
        Loading records…
      </div>
    );
  }

  const hasConflicts =
    resolution.conflicts.length > 0 ||
    resolution.multiValue.length > 0 ||
    assocConflicts.length > 0;
  const autoResolvedCount =
    resolution.autoResolved.length + assocAutoResolved.length;
  const matchingCount = resolution.matching.length + assocMatching.length;

  return (
    <div className='flex flex-1 flex-col gap-4 min-h-0 md:flex-row md:gap-6'>
      {/* Mobile (< md): preview collapses to a disclosure at the top of the
          column. Desktop (≥ md): persistent left sidebar. */}
      <MergePreviewCollapsible
        previewObject={previewObject}
        schema={schema}
        detailOrderKeys={detailOrderKeys}
      />
      <MergePreviewPanel
        previewObject={previewObject}
        schema={schema}
        detailOrderKeys={detailOrderKeys}
      />

      <section className='flex-1 overflow-y-auto'>
        {hasConflicts ? (
          <>
            {orderedConflicts.map(conflict => (
              <ConflictRow
                key={conflict.propertyName}
                conflict={conflict}
                schema={schema}
                currentWinner={
                  singlePropertyResolutions[conflict.propertyName]?.winner ??
                  conflict.winner
                }
                onSelectWinner={handleSelectWinner}
              />
            ))}

            {resolution.multiValue.map(multi => {
              const current =
                multiPropertyResolutions[multi.propertyName] ?? multi;
              return (
                <MultiRow
                  key={multi.propertyName}
                  multi={multi}
                  schema={schema}
                  current={current}
                  onToggleItem={handleToggleMultiItem}
                />
              );
            })}

            <AssocConflictPickers conflicts={assocConflicts} orgId={orgId} />
          </>
        ) : (
          <div className='mb-6 flex items-center justify-center py-8 text-body-small text-[var(--color-text-tertiary)]'>
            No conflicts to resolve — everything is auto-resolved or matching.
          </div>
        )}

        <CollapsibleSection title='Auto-resolved' count={autoResolvedCount}>
          {resolution.autoResolved.map(r => (
            <PropertyValueRow
              key={r.propertyName}
              label={schema[r.propertyName]?.label ?? r.propertyName}
              value={
                (r.winner === 'canonical' ? r.canonicalValue : r.sourceValue)
                  ?.value
              }
              propertyType={schema[r.propertyName]}
            />
          ))}
          <AssocAutoResolvedList items={assocAutoResolved} orgId={orgId} />
        </CollapsibleSection>

        <CollapsibleSection title='Matching' count={matchingCount}>
          {resolution.matching.map(r => (
            <PropertyValueRow
              key={r.propertyName}
              label={schema[r.propertyName]?.label ?? r.propertyName}
              value={r.canonicalValue?.value}
              propertyType={schema[r.propertyName]}
            />
          ))}
          <AssocMatchingList items={assocMatching} orgId={orgId} />
        </CollapsibleSection>
      </section>
    </div>
  );
}
